import React from 'react';
import { Check } from 'lucide-react';

/**
 * Cycle de vie complet d'un mandat (7 étapes).
 *
 * Pré-signature :
 *   1. Brouillon (draft)
 *   2. PDF généré (generated)
 *   3. Envoyé (sent)
 *   4. Signé / Actif (acknowledged | actif | active | signed)
 *
 * Post-signature (cycle commercial) :
 *   5. Offre reçue (offre | offer_received)
 *   6. Compromis signé (compromis)
 *   7. Vendu (vendu | sold)
 *
 * Tant qu'un mandat est actif sans offre, on s'arrête à l'étape 4. Dès qu'une
 * offre est reçue, le statut backend passe automatiquement à "offre" (cf.
 * routers/offers.py L799-806), et la barre suit visuellement la progression.
 */
const STAGES = [
    { key: 'draft', label: 'Brouillon', short: 'Broui.' },
    { key: 'generated', label: 'PDF généré', short: 'PDF' },
    { key: 'sent', label: 'Envoyé', short: 'Envoyé' },
    { key: 'acknowledged', label: 'Signé', short: 'Signé' },
    { key: 'offre', label: 'Offre reçue', short: 'Offre' },
    { key: 'compromis', label: 'Compromis', short: 'Compromis' },
    { key: 'vendu', label: 'Vendu', short: 'Vendu' },
];

const STATUS_MAP = {
    // Pré-signature variants
    'brouillon': 'draft',
    'pdf_generated': 'generated',
    // Post-signature variants → tous mappés sur "Signé" (étape 4)
    'active': 'acknowledged',
    'actif': 'acknowledged',
    'signed': 'acknowledged',
    // Offre variants
    'offer_received': 'offre',
    'offer': 'offre',
    'offre_recue': 'offre',
    // Compromis variants
    'sous_compromis': 'compromis',
    'under_contract': 'compromis',
    // Vente variants
    'sold': 'vendu',
    'closed': 'vendu',
    'terminated': 'vendu',
};

const getStageIndex = (status) => {
    const normalized = STATUS_MAP[status] || status;
    const idx = STAGES.findIndex((s) => s.key === normalized);
    return idx >= 0 ? idx : 0;
};

/**
 * Compact timeline for mandate cards (list view).
 * Shows 7 dots + connecting line, with tooltip per dot.
 */
export function MandateTimelineCompact({ status }) {
    const currentIdx = getStageIndex(status);

    return (
        <div className="flex items-center gap-0" data-testid="mandate-timeline-compact">
            {STAGES.map((stage, idx) => {
                const isDone = idx < currentIdx;
                const isCurrent = idx === currentIdx;
                return (
                    <React.Fragment key={stage.key}>
                        <div className="relative group">
                            <div className={`w-2 h-2 rounded-full transition-all ${
                                isDone ? 'bg-green-400' :
                                isCurrent ? 'bg-[#FF914D] ring-2 ring-[#FF914D]/30' :
                                'bg-white/10'
                            }`} />
                            <div className="absolute -top-7 left-1/2 -translate-x-1/2 bg-slate-800 text-white text-[9px] px-1.5 py-0.5 rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10 border border-white/10">
                                {stage.short}
                            </div>
                        </div>
                        {idx < STAGES.length - 1 && (
                            <div className={`w-2 sm:w-3 h-[1.5px] ${
                                idx < currentIdx ? 'bg-green-400/60' : 'bg-white/10'
                            }`} />
                        )}
                    </React.Fragment>
                );
            })}
        </div>
    );
}

/**
 * Full timeline for mandate detail page.
 * 7 labeled steps with check marks, progress line that fills as the mandate advances.
 */
export function MandateTimelineFull({ status }) {
    const currentIdx = getStageIndex(status);

    return (
        <div className="w-full" data-testid="mandate-timeline-full">
            <div className="flex items-center justify-between relative">
                {/* Background line */}
                <div className="absolute top-3.5 left-0 right-0 h-[2px] bg-white/5 rounded-full" />
                {/* Progress line — passe par-dessus tous les stades atteints */}
                <div
                    className="absolute top-3.5 left-0 h-[2px] rounded-full bg-gradient-to-r from-green-400 via-[#FF914D] to-[#FF914D] transition-all duration-700 ease-out"
                    style={{ width: `${(currentIdx / (STAGES.length - 1)) * 100}%` }}
                />

                {STAGES.map((stage, idx) => {
                    const isDone = idx < currentIdx;
                    const isCurrent = idx === currentIdx;

                    return (
                        <div key={stage.key} className="flex flex-col items-center relative z-10 px-1">
                            <div className={`w-6 h-6 sm:w-7 sm:h-7 rounded-full flex items-center justify-center transition-all duration-300 ${
                                isDone
                                    ? 'bg-green-500 shadow-lg shadow-green-500/30'
                                    : isCurrent
                                        ? 'bg-gradient-to-br from-[#681793] to-[#FF914D] shadow-lg shadow-[#FF914D]/30 ring-3 ring-[#FF914D]/20'
                                        : 'bg-white/5 border border-white/10'
                            }`}>
                                {isDone ? (
                                    <Check className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-white" strokeWidth={3} />
                                ) : (
                                    <span className={`text-[9px] sm:text-[10px] font-bold ${isCurrent ? 'text-white' : 'text-white/30'}`}>
                                        {idx + 1}
                                    </span>
                                )}
                            </div>
                            <span className={`text-[9px] sm:text-[10px] mt-1.5 font-medium text-center leading-tight ${
                                isDone ? 'text-green-400' :
                                isCurrent ? 'text-[#FF914D]' :
                                'text-white/25'
                            }`}>
                                {stage.short}
                            </span>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
